import classnames from 'classnames'
import * as React from 'react'

export const buttonSizeArray = ['lg', 'xl'] as const

export type ButtonSize = typeof buttonSizeArray[number]

export const buttonStyleArray = [
  'blue',
  'red',
  'navy',
  'navy--inverse',
  'disabled',
] as const

export type ButtonStyle = typeof buttonStyleArray[number]

export const buttonTypeArray = ['button', 'submit', 'reset'] as const

export type ButtonType = typeof buttonTypeArray[number]

export interface ButtonProps {
  /** Event handler for the button click */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  /** Color scheme to use for the button */
  style?: ButtonStyle
  /** Optional classes to add to the button component */
  displayClasses?: string
  /** Whether the button is disabled and has disabled styling */
  disabled?: boolean
  /** Size of button */
  size?: ButtonSize
  /** Sets button's HTML type property */
  type?: ButtonType
  /** when set, displays a rounded edge pill button. this can be used in
   conjunction with other `style` attributes. */
  pill?: boolean
  /** when set, displays a "link" button, which has no borders but is of the
   same size as a regular button. can be used with other `style` attributes. */
  link?: boolean
  /** The content to render inside of the button (usually text) */
  children?: React.ReactNode
  /** A web address that can be internal or external. */
  url?: string
  /** External web address need a target and rel. */
  external?: boolean
  /** When set, provides button hover text. */
  title?: string
  /** Optional identifier for the AQA */
  dataSeleniumTarget?: string
}

export const BUTTON_IDENTIFIER = 'button_window'

const Button: React.FunctionComponent<ButtonProps> = (props) => {
  const {
    onClick,
    style,
    type,
    displayClasses,
    disabled,
    size,
    pill,
    link,
    url,
    external,
    dataSeleniumTarget,
    ...rest
  } = props
  const styleCls = style ? `button--${style}` : ''
  const cls = classnames(
    'button',
    BUTTON_IDENTIFIER,
    styleCls,
    size,
    displayClasses,
    {
      'button--pill': pill,
      'button--link': link,
      disabled: disabled,
    }
  )

  const target = external ? '_blank' : undefined
  const rel = external ? 'noopener noreferrer' : undefined

  return url ? (
    <a
      className={cls}
      href={url}
      target={target}
      rel={rel}
      onClick={onClick}
      data-selenium-target={dataSeleniumTarget}
      {...rest}
    >
      {props.children}
    </a>
  ) : (
    <button
      type={type}
      className={cls}
      aria-disabled={disabled}
      onClick={(e) => {
        if (disabled || !onClick) {
          return
        }
        return onClick(e)
      }}
      data-selenium-target={dataSeleniumTarget}
      {...rest}
    >
      {props.children}
    </button>
  )
}

export default Button
